import { toMajorUnits } from "@babysits/islands/src/shared/utils/currency-formatter";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(timezone);

export const initBookingCreate = () => {
  const bookingCreate = document.getElementById("booking-create") as HTMLDivElement;

  if (!bookingCreate) {
    return;
  }

  const startAt = bookingCreate.querySelector(".start_at") as HTMLInputElement;
  const duration = bookingCreate.querySelector(".duration") as HTMLInputElement;

  const dateSelectorFrom = bookingCreate.querySelector(".date-selector-from") as HTMLInputElement;
  const timeSelectorFrom = bookingCreate.querySelector(".time-selector-from") as HTMLSelectElement;
  const dateSelectorTo = bookingCreate.querySelector(".date-selector-to") as HTMLInputElement;
  const timeSelectorTo = bookingCreate.querySelector(".time-selector-to") as HTMLSelectElement;

  const dateSelectorFromIcon = bookingCreate.querySelector(
    ".date-selector-from-icon",
  ) as HTMLInputElement;
  const dateSelectorToIcon = bookingCreate.querySelector(
    ".date-selector-to-icon",
  ) as HTMLInputElement;

  dateSelectorFromIcon.addEventListener("click", () => {
    dateSelectorFrom.showPicker();
  });
  dateSelectorToIcon.addEventListener("click", () => {
    dateSelectorTo.showPicker();
  });

  const submitButton = bookingCreate.querySelector('[type="submit"]') as HTMLButtonElement;

  // Store the original timezone mismatch alert template
  const timezoneAlertText = bookingCreate.querySelector(".timezone-mismatch-text") as HTMLElement;
  const originalTimezoneMismatchMessage = timezoneAlertText?.textContent || "";

  // Handle babysitter radio button selection
  const babysitterRadios = bookingCreate.querySelectorAll(".babysitter-radio");

  // Helper function to convert from smallest unit to standard unit and format as currency
  function formatAmount(amountInSmallestUnit: string, currencyCode: string): string {
    const locale = document.documentElement.lang;
    // ISO 4217 digits, not Intl/CLDR (see assets/AGENTS.md)
    const amount = toMajorUnits(parseFloat(amountInSmallestUnit), currencyCode);

    // Format as currency
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).format(amount);
  }

  // Set up event listeners for radio button changes
  babysitterRadios.forEach((radio) => {
    radio.addEventListener("change", () => {
      // Recalculate totals when babysitter changes
      updateBooking();
      updateTimezoneLabels();
    });
  });

  const hostnameBase = window.location.hostname.replace(/^[^.]*./, "");

  // Store original option texts
  const originalTimeSelectorFromOptions: string[] = [];
  const originalTimeSelectorToOptions: string[] = [];

  // Save original option texts
  Array.from(timeSelectorFrom.options).forEach((option) => {
    originalTimeSelectorFromOptions.push(option.textContent || "");
  });
  Array.from(timeSelectorTo.options).forEach((option) => {
    originalTimeSelectorToOptions.push(option.textContent || "");
  });

  // Update timezone labels to show timezone abbreviation when there's a mismatch
  function updateTimezoneLabels() {
    // Get the selected babysitter radio button
    const selectedBabysitter = bookingCreate.querySelector(
      ".babysitter-radio:checked",
    ) as HTMLInputElement;
    if (!selectedBabysitter) {
      return;
    }

    const babysitterTimezone = selectedBabysitter.dataset.timezone;
    const babysitterFirstName = selectedBabysitter.dataset.firstname;

    const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const date =
      dateSelectorFrom.value && timeSelectorFrom.value
        ? new Date(`${dateSelectorFrom.value} ${timeSelectorFrom.value}`)
        : new Date();

    // Get the timezone abbreviations for both timezones
    const currentTzAbbr =
      new Intl.DateTimeFormat(undefined, {
        timeZone: currentTimezone,
        timeZoneName: "short",
      })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value || "";

    const babysitterTzAbbr =
      new Intl.DateTimeFormat(undefined, {
        timeZone: babysitterTimezone,
        timeZoneName: "short",
      })
        .formatToParts(date)
        .find((part) => part.type === "timeZoneName")?.value || "";

    // Check if timezones are different
    const timezoneMismatch = babysitterTzAbbr !== currentTzAbbr;

    // Show/hide timezone mismatch alert and update with babysitter name
    const timezoneAlert = bookingCreate.querySelector(".timezone-mismatch-alert") as HTMLElement;
    if (timezoneAlert) {
      if (timezoneMismatch) {
        timezoneAlert.classList.remove("d-none");
        // Replace {first_name} placeholder with actual babysitter name
        if (timezoneAlertText && babysitterFirstName) {
          timezoneAlertText.textContent = originalTimezoneMismatchMessage.replace(
            "{first_name}",
            babysitterFirstName,
          );
        }
      } else {
        timezoneAlert.classList.add("d-none");
      }
    }

    // Only show timezone labels if the abbreviations are different
    if (timezoneMismatch && babysitterTzAbbr) {
      // Append timezone abbreviation to each option
      Array.from(timeSelectorFrom.options).forEach((option, index) => {
        option.textContent = `${originalTimeSelectorFromOptions[index]} ${babysitterTzAbbr}`;
      });
      Array.from(timeSelectorTo.options).forEach((option, index) => {
        option.textContent = `${originalTimeSelectorToOptions[index]} ${babysitterTzAbbr}`;
      });
    } else {
      // Restore original option texts
      Array.from(timeSelectorFrom.options).forEach((option, index) => {
        option.textContent = originalTimeSelectorFromOptions[index];
      });
      Array.from(timeSelectorTo.options).forEach((option, index) => {
        option.textContent = originalTimeSelectorToOptions[index];
      });
    }
  }

  /* Convert input datetime to utc and fill hidden datetime field */
  function updateBooking() {
    const dateFrom = dateSelectorFrom.value;
    const timeFrom = timeSelectorFrom.value;
    const dateTo = dateSelectorTo.value;
    const timeTo = timeSelectorTo.value;

    const warningMinimalDuration = bookingCreate.querySelector(
      ".warning-minimum-duration",
    ) as HTMLParagraphElement;
    warningMinimalDuration.style.display = "none";

    const warningMaximumDuration = bookingCreate.querySelector(
      ".warning-maximum-duration",
    ) as HTMLParagraphElement;
    warningMaximumDuration.style.display = "none";

    const warningEndTimeBeforeStartTime = bookingCreate.querySelector(
      ".warning-end-time-before-start-time",
    ) as HTMLParagraphElement;
    warningEndTimeBeforeStartTime.style.display = "none";

    const warningStartTimeBeforeNow = bookingCreate.querySelector(
      ".warning-start-time-before-now",
    ) as HTMLParagraphElement;
    warningStartTimeBeforeNow.style.display = "none";

    submitButton.disabled = false;

    if (dateFrom && timeFrom && dateTo && timeTo) {
      // Get the selected babysitter timezone
      const selectedBabysitter = bookingCreate.querySelector(
        ".babysitter-radio:checked",
      ) as HTMLInputElement;
      const babysitterTimezone = selectedBabysitter?.dataset.timezone;

      // Always use babysitter timezone
      const dayjs_datetimeFrom = dayjs.tz(`${dateFrom} ${timeFrom}`, babysitterTimezone);

      const dayjs_datetimeTo = dayjs.tz(`${dateTo} ${timeTo}`, babysitterTimezone);

      const dayjs_datetime_now = dayjs();

      /* Prevent user from selecting a datetimeFrom in the past */
      if (dayjs_datetimeFrom.isBefore(dayjs_datetime_now)) {
        submitButton.disabled = true;

        warningStartTimeBeforeNow.style.display = "block";

        return;
      }

      /* Prevent user from selecting datetimeTo in the past */
      if (dayjs_datetimeTo < dayjs_datetime_now) {
        submitButton.disabled = true;

        return;
      }

      /* Prevent user from selecting a timeTo similar to or before timeFrom */
      if (dayjs_datetimeTo <= dayjs_datetimeFrom) {
        submitButton.disabled = true;

        warningEndTimeBeforeStartTime.style.display = "block";

        return;
      }

      // Make sure to set the startAt time in UTC, so our backend will receive the time in UTC
      startAt.value = dayjs_datetimeFrom.utc().format();

      /* Calculate number of hours */
      const dayjsDuration = dayjs.duration(dayjs_datetimeTo.diff(dayjs_datetimeFrom));

      const durationAsMinutes = dayjsDuration.asMinutes();

      /* Do now allow a duration less than 1 hour */
      if (durationAsMinutes < 60) {
        submitButton.disabled = true;

        warningMinimalDuration.style.display = "block";

        return;
      }

      /* Do now allow a duration more than 24 hours */
      if (durationAsMinutes > 1440) {
        submitButton.disabled = true;

        warningMaximumDuration.style.display = "block";

        return;
      }

      duration.value = durationAsMinutes.toString();

      updateTotalAmountCalculation();

      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-dateSelectorFrom=" +
        dateSelectorFrom.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-timeSelectorFrom=" +
        timeSelectorFrom.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-dateSelectorTo=" +
        dateSelectorTo.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-timeSelectorTo=" +
        timeSelectorTo.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-startAt=" +
        startAt.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
      // biome-ignore lint/suspicious/noDocumentCookie: <Fix after all files will be refactored to biome rules>
      document.cookie =
        "booking-duration=" +
        duration.value +
        "; max-age=2592000; path=/; domain=" +
        hostnameBase +
        "; secure; samesite=lax";
    }
  }

  function updateDateSelectorToValueMinAndMax() {
    const fromDate = dayjs(dateSelectorFrom.value);

    dateSelectorTo.min = fromDate.format("YYYY-MM-DD");
    dateSelectorTo.max = fromDate.add(1, "days").format("YYYY-MM-DD");
  }

  function updateTotalAmountCalculation() {
    if (!duration.value) {
      return;
    }

    // Get the selected babysitter radio button
    const selectedBabysitter = bookingCreate.querySelector(
      ".babysitter-radio:checked",
    ) as HTMLInputElement;
    if (!selectedBabysitter) {
      return;
    }

    const dayjsDuration = dayjs.duration(parseInt(duration.value, 10), "minutes");

    const durationAsHours = dayjsDuration.asHours();
    const durationHours = Math.floor(dayjsDuration.asHours());
    const durationMinutes = dayjsDuration.minutes();

    const calculationAmount = bookingCreate.querySelector(".calculation-amount") as HTMLDivElement;
    const calculationHours = calculationAmount.querySelector(".hours") as HTMLSpanElement;
    const calculationMinutes = calculationAmount.querySelector(".minutes") as HTMLSpanElement;
    const calculationMinutesLabel = calculationAmount.querySelector(
      ".minutes-label",
    ) as HTMLSpanElement;

    calculationHours.textContent = durationHours.toString();
    calculationMinutes.textContent = durationMinutes.toString();

    calculationMinutes.style.display = "inline-block";
    calculationMinutesLabel.style.display = "inline-block";
    if (durationMinutes === 0) {
      calculationMinutes.style.display = "none";
      calculationMinutesLabel.style.display = "none";
    }

    const currency = selectedBabysitter.dataset.rateCurrency ?? "";
    const isPremium = bookingCreate.dataset.isPremium === "true";

    // Calculate amount (per hour and total)
    const displayedPerHourAmount = calculationAmount.querySelector(
      ".displayed-per-hour-amount",
    ) as HTMLSpanElement;
    const displayedAmount = calculationAmount.querySelector(".displayed-amount") as HTMLDivElement;

    displayedPerHourAmount.textContent = formatAmount(
      selectedBabysitter.dataset.rateAmount ?? "0",
      currency,
    );
    displayedAmount.textContent = formatAmount(
      (parseFloat(selectedBabysitter.dataset.rateAmount ?? "0") * durationAsHours).toString(),
      currency,
    );

    // Calculate Fee
    const feeDataAttr = isPremium
      ? (selectedBabysitter.dataset.rateFeePremium ?? "0")
      : (selectedBabysitter.dataset.rateFeeStandard ?? "0");
    const calculationFee = bookingCreate.querySelector(".calculation-fee") as HTMLDivElement;
    const feeDisplayedAmount = calculationFee.querySelector(".displayed-amount") as HTMLDivElement;
    feeDisplayedAmount.textContent = formatAmount(
      (parseFloat(feeDataAttr) * durationAsHours).toString(),
      currency,
    );

    // Calculate Total
    const totalDataAttr = isPremium
      ? (selectedBabysitter.dataset.rateTotalPremium ?? "0")
      : (selectedBabysitter.dataset.rateTotalStandard ?? "0");
    const calculationTotal = bookingCreate.querySelector(".calculation-total") as HTMLDivElement;
    const totalDisplayedAmount = calculationTotal.querySelector(
      ".displayed-amount",
    ) as HTMLDivElement;
    totalDisplayedAmount.textContent = formatAmount(
      (parseFloat(totalDataAttr) * durationAsHours).toString(),
      currency,
    );

    // Calculate Savings
    const formattedSavings = formatAmount(
      (
        parseFloat(selectedBabysitter.dataset.rateFeePremiumSavings ?? "0") * durationAsHours
      ).toString(),
      currency,
    );

    const calculationSavings = bookingCreate.querySelector(
      ".calculation-savings",
    ) as HTMLDivElement;
    const savingsTranslation = calculationSavings.dataset.translation as string;
    const translatedFormattedSavings = savingsTranslation.replace("{amount}", formattedSavings);
    const savingsDisplayedAmount = calculationSavings.querySelector(
      ".displayed-amount",
    ) as HTMLDivElement;

    savingsDisplayedAmount.textContent = translatedFormattedSavings;
  }

  /* onchange dateSelectorFrom: set dateSelectorTo to match dateSelectorFrom. Only allow min=to and max=1day */
  dateSelectorFrom.addEventListener("change", () => {
    dateSelectorTo.value = dateSelectorFrom.value;
    updateDateSelectorToValueMinAndMax();
  });

  dateSelectorFrom.addEventListener("change", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  timeSelectorFrom.addEventListener("change", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  dateSelectorTo.addEventListener("change", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  timeSelectorTo.addEventListener("change", () => {
    updateBooking();
    updateTimezoneLabels();
  });

  dateSelectorFrom.addEventListener("keyup", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  timeSelectorFrom.addEventListener("keyup", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  dateSelectorTo.addEventListener("keyup", () => {
    updateBooking();
    updateTimezoneLabels();
  });
  timeSelectorTo.addEventListener("keyup", () => {
    updateBooking();
    updateTimezoneLabels();
  });

  /* Set values from cookies */
  const dateSelectorFromCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-dateSelectorFrom="))
    ?.split("=")[1];
  const timeSelectorFromCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-timeSelectorFrom="))
    ?.split("=")[1];
  const dateSelectorToCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-dateSelectorTo="))
    ?.split("=")[1];
  const timeSelectorToCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-timeSelectorTo="))
    ?.split("=")[1];
  const startAtCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-startAt="))
    ?.split("=")[1];
  const durationCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("booking-duration="))
    ?.split("=")[1];

  if (
    dateSelectorFromCookie &&
    timeSelectorFromCookie &&
    dateSelectorToCookie &&
    timeSelectorToCookie &&
    startAtCookie &&
    durationCookie
  ) {
    if (timeSelectorFrom && timeSelectorTo) {
      // Set the fields we can always set
      timeSelectorFrom.value = timeSelectorFromCookie;
      timeSelectorTo.value = timeSelectorToCookie;
    }

    const dayjs_start = dayjs(startAtCookie);

    if (!dayjs_start.isBefore(dayjs())) {
      // startAtCookie is in the future: set all other fields as well
      dateSelectorFrom.value = dateSelectorFromCookie;

      dateSelectorTo.value = dateSelectorToCookie;
      updateDateSelectorToValueMinAndMax();

      startAt.value = startAtCookie;
      duration.value = durationCookie;
    }

    // dateSelectorTo is now always visible
  }

  // Initial booking update and timezone labels
  updateBooking();
  updateTimezoneLabels();
};
